"use client";

import { useEffect, useState } from "react";
import clsx from "clsx";

/**
 * LcCompositionProgress — slim banner tracking the bank LC being assembled
 * across T2 screens 4.5 / 4.10 / 4.11 / 4.13. The bar progresses
 * monotonically — stepping backward (Shift+N rewind) does NOT regress the
 * displayed value; the bar simply holds at its highest-seen pct.
 *
 * Continuity across mounts: a module-level high-water mark (`lastShownPct`)
 * remembers where the bar ended up on its previous render. The next mount
 * reads it and uses MAX(lastShownPct, startPct) as the tween origin — so
 * the audience sees one continuous progression instead of three resets.
 *
 * On forward progression (target > lastShownPct), the bar tweens
 * lastShownPct → target. On reverse navigation (target < lastShownPct),
 * the bar holds at lastShownPct with no animation. This mirrors the
 * narrative claim that LC composition is irreversible: once integrated,
 * it stays integrated.
 */

// Module-level high-water mark — survives component unmount/remount on
// SceneRouter case-statement transitions. Resets to 0 when the page is
// fully reloaded (which restarts the scenario anyway).
let lastShownPct = 0;

/** Test helper: reset the high-water mark. Not used by production code. */
export function __resetLcCompositionProgress() {
  lastShownPct = 0;
}

export function LcCompositionProgress({
  startPct,
  targetPct,
  label,
  className,
}: {
  startPct: number;
  targetPct: number;
  label: string;
  className?: string;
}) {
  // On mount, snap to the highest-seen point so far. If we're stepping
  // forward, that's `max(startPct, lastShownPct)`. If we're holding /
  // stepping back, the bar stays at lastShownPct without animating
  // downward.
  const tweenFrom = Math.max(startPct, lastShownPct);
  const tweenTo = Math.max(targetPct, lastShownPct);
  const [pct, setPct] = useState(tweenFrom);

  useEffect(() => {
    if (tweenTo <= tweenFrom) {
      // No forward progression to animate — hold at the high-water mark.
      setPct(tweenFrom);
      lastShownPct = tweenFrom;
      return;
    }
    const DURATION = 1200;
    const begin = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - begin) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3);
      const v = tweenFrom + (tweenTo - tweenFrom) * eased;
      setPct(v);
      lastShownPct = Math.max(lastShownPct, v);
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [tweenFrom, tweenTo]);

  return (
    <div className={clsx("glass flex items-center gap-4 p-3", className)}>
      <div className="flex flex-1 flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">
            LC composition · in progress
          </p>
          <p className="font-mono text-[10px] text-state-financed">
            {Math.round(pct)}%
          </p>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-orbitra-ink/60">
          <div
            className="h-full rounded-full bg-state-financed shadow-[0_0_12px_var(--state-financed-glow)] transition-[width]"
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className="mt-0.5 text-[11px] text-orbitra-paper">{label}</p>
      </div>
    </div>
  );
}
